import React, { FC } from 'react';
import sumBy from 'lodash/sumBy';
import ModalDialog from '../components/modal/ModalDialog';
import './CheckBeforeAddingModal.scss';
import { Button, ButtonSize, ButtonTheme } from './Button';
import { FallbackPriceTier, getPriceTierLabel, PriceTier } from '../../../models/PriceTier';
import { Trans, useTranslation } from 'react-i18next';
import HorizontalRuler from './HorizontalRuler';
import { ProductOut } from '../../store/api';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { ExpandableTable } from './TableRow';

export type CheckBeforeAddingModalProps = {
    readonly target: ListActionTarget | undefined;
    readonly requestedPriceTier: PriceTier | undefined;
    readonly fallbackPriceTiers: FallbackPriceTier[];
    readonly alreadyInListProducts: ProductOut[];
    readonly preOrderedProducts: ProductOut[];
    readonly onProceed: () => Promise<void>;
    readonly onCancel: () => void;
    readonly isLoading: boolean;
    readonly customTitle?: string;
};

export const CheckBeforeAddingModal: FC<CheckBeforeAddingModalProps> = ({
    target,
    requestedPriceTier,
    fallbackPriceTiers,
    alreadyInListProducts,
    preOrderedProducts,
    onProceed,
    onCancel,
    isLoading,
    customTitle
}) => {
    const HEIGHT_SPACER = 5;
    const { t } = useTranslation();

    if (
        (fallbackPriceTiers.length === 0 && alreadyInListProducts.length === 0 && preOrderedProducts.length === 0) ||
        (alreadyInListProducts.length === 0 && preOrderedProducts.length === 0 && !requestedPriceTier)
    ) {
        return null;
    }

    const submitLabel = !target ? t('proceed') : target === ListActionTarget.List ? t('addToList') : t('addToCart');
    const amountOfFallbackPrices = sumBy(
        fallbackPriceTiers,
        (fallbackPrice: FallbackPriceTier) => fallbackPrice.amount
    );

    return (
        <ModalDialog
            show={true}
            title={customTitle ?? t('components.checkBeforeAddingModal.title')}
            titleClassName="title"
            wrapClassName="check-before-adding-modal"
            boldHeader={false}
        >
            {requestedPriceTier && fallbackPriceTiers.length > 0 ? (
                <>
                    <HorizontalRuler heightSpacer={HEIGHT_SPACER} />
                    <div className="subtitle">
                        <Trans
                            i18nKey="components.checkBeforeAddingModal.availabilityTitle"
                            components={{
                                br: <br />
                            }}
                        />
                    </div>
                    <Trans
                        i18nKey="components.checkBeforeAddingModal.availabilityMessage"
                        count={amountOfFallbackPrices}
                        components={{
                            p: <p className="description" />,
                            strong: <strong />,
                            i: <i />
                        }}
                        values={{
                            count: amountOfFallbackPrices,
                            priceTierLabel: getPriceTierLabel(requestedPriceTier, t)
                        }}
                    />
                    <ul>
                        {fallbackPriceTiers.map(fallbackPrice => (
                            <li key={`${fallbackPrice.amount}x${getPriceTierLabel(fallbackPrice.priceTier, t)}`}>
                                {t('components.checkBeforeAddingModal.fallbackPriceTier', {
                                    amount: fallbackPrice.amount,
                                    priceTierLabel: getPriceTierLabel(fallbackPrice.priceTier, t)
                                })}
                            </li>
                        ))}
                    </ul>
                </>
            ) : null}
            {alreadyInListProducts.length > 0 ? (
                <>
                    <HorizontalRuler heightSpacer={HEIGHT_SPACER} />
                    <div className="subtitle">
                        {t('components.checkBeforeAddingModal.alreadyInListTitle', {
                            count: alreadyInListProducts.length
                        })}
                    </div>
                    <p className="description">
                        {t('components.checkBeforeAddingModal.alreadyInListMessage', {
                            context: target === ListActionTarget.Cart ? 'cart' : 'list',
                            count: alreadyInListProducts.length
                        })}
                    </p>
                    <ExpandableTable products={alreadyInListProducts} />
                    <br />
                </>
            ) : null}
            {preOrderedProducts.length > 0 ? (
                <>
                    <HorizontalRuler heightSpacer={HEIGHT_SPACER} />
                    <div className="subtitle">{t('components.checkBeforeAddingModal.preOrderTitle')}</div>
                    <p className="description">
                        {t('components.checkBeforeAddingModal.preOrderMessage', {
                            count: preOrderedProducts.length
                        })}
                    </p>
                    <ExpandableTable products={preOrderedProducts} />
                    <br />
                </>
            ) : null}
            <div className="actions">
                <Button
                    onClick={onCancel}
                    text={t('cancel')}
                    theme={ButtonTheme.POSITIVE_INVERT}
                    size={ButtonSize.LARGE}
                    className="cancel-button"
                    disabled={isLoading}
                />
                <Button
                    onClick={onProceed}
                    text={submitLabel}
                    className="proceed-button"
                    data-testid="check-before-adding-proceed"
                    size={ButtonSize.LARGE}
                    disabled={isLoading}
                    isLoading={isLoading}
                />
            </div>
        </ModalDialog>
    );
};
